import React, {useEffect, useRef, useState, useCallback, useMemo} from "react";
import {
    Checkmark,
    ColorButton,
    CustomizationWrap,
    DropDown,
    RadioInput,
    RadioLabel,
    SelectorWrapper,
} from "./Customization.styles";
import {useForm} from "components/molecules/Form";
import Field from "components/molecules/Field";
import Form from "components/molecules/Form/Form";
import {IoMdArrowDropdown} from "react-icons/io";
import tick from "assets/tick.svg";
import Button from "components/molecules/Button";
import {FaRegEdit} from "react-icons/fa";
import {IoIosArrowDown} from "react-icons/io";
import {useTheme} from "Context/themeContext";

// Utility functions for better code organization
const isValidHexColor = (color) => {
    const hexColorRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
    return hexColorRegex.test(color);
};

const getInitialColorState = (savedColor, colors) => {
    return savedColor && isValidHexColor(savedColor) ? savedColor : colors[0];
};

// Constants for better maintainability
const THEME_COLORS = ["#22B573", "#3392CC", "#E4042C", "#836529", "#e404bf", "#0E79BF", "#DE5B9E", "#C6164F"];
const SAVE_DELAY_MS = 800;

const Customization = () => {
    const [form] = useForm();
    const [isDropdown, setIsDropdown] = useState(false);
    const [btnDropdown, setBtnDropdown] = useState(false);
    const dropdownRef = useRef(null);
    const dropdownIconRef = useRef(null);
    const btnDropdownRef = useRef(null);
    const btnDropdownIconRef = useRef(null);
    const handleClickOutside = event => {
        if (
            dropdownRef.current &&
            !dropdownRef.current.contains(event.target) &&
            dropdownIconRef.current &&
            !dropdownIconRef.current.contains(event.target)
        ) {
            setIsDropdown(false);
        }
        if (
            btnDropdownRef.current &&
            !btnDropdownRef.current.contains(event.target) &&
            btnDropdownIconRef.current &&
            !btnDropdownIconRef.current.contains(event.target)
        ) {
            setBtnDropdown(false);
        }
    };
    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);
    const savedColor = localStorage.getItem("themeColor");
    const initialColor = getInitialColorState(savedColor, THEME_COLORS);

    const [selectedColor, setSelectedColor] = useState(initialColor);
    const [customColor, setCustomColor] = useState(initialColor);
    const [tempSelectedColor, setTempSelectedColor] = useState(initialColor); // Temporary selection before save
    const [tempCustomColor, setTempCustomColor] = useState(initialColor); // Temporary custom color before save
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const {toggleTheme} = useTheme();
    // Memoized handlers for better performance
    const handleCheckboxChange = useCallback((event, color) => {
        if (!isValidHexColor(color)) {
            console.warn('Invalid color selected:', color);
            return;
        }
        setTempSelectedColor(color);
        setTempCustomColor(color); // Update temporary custom color to match selected preset
        setHasUnsavedChanges(true);
    }, []);

    const handleCustomColorChange = useCallback((event) => {
        const color = event.target.value;
        if (!isValidHexColor(color)) {
            console.warn('Invalid custom color:', color);
            return;
        }
        setTempCustomColor(color);
        setTempSelectedColor(color);
        setHasUnsavedChanges(true);
    }, []);

    const handleSaveChanges = async () => {
        try {
            setIsSaving(true);
            
            // Validate color before applying
            if (!isValidHexColor(tempSelectedColor)) {
                throw new Error('Invalid color format');
            }
            
            // Update local states optimistically
            setSelectedColor(tempSelectedColor);
            setCustomColor(tempCustomColor);
            
            // Apply theme changes with proper error handling
            await new Promise((resolve, reject) => {
                setTimeout(() => {
                    try {
                        toggleTheme(tempSelectedColor);
                        localStorage.setItem("themeColor", tempSelectedColor);
                        resolve();
                    } catch (error) {
                        reject(error);
                    }
                }, SAVE_DELAY_MS);
            });
            
            // Success state
            setHasUnsavedChanges(false);
            
        } catch (error) {
            console.error('Theme application failed:', error);
            // Revert optimistic updates on error
            setSelectedColor(selectedColor);
            setCustomColor(customColor);
            // Could add toast notification here for user feedback
        } finally {
            setIsSaving(false);
        }
    };

    const handleDiscardChanges = useCallback(() => {
        setTempSelectedColor(selectedColor);
        setTempCustomColor(customColor);
        setHasUnsavedChanges(false);
    }, [selectedColor, customColor]);

    // Computed values for better performance
    const isPresetColorSelected = useMemo(() => {
        return THEME_COLORS.includes(tempSelectedColor);
    }, [tempSelectedColor]);

    const canSaveChanges = useMemo(() => {
        return hasUnsavedChanges && isValidHexColor(tempSelectedColor) && !isSaving;
    }, [hasUnsavedChanges, tempSelectedColor, isSaving]);
    const dropdownValue = [
        {
            value: "Light-Mood",
            label: "Light Mood",
        },
        {
            value: "Dark-Mood",
            label: "Dark Mood",
        },
    ];
    return (
        <CustomizationWrap className={isSaving ? 'saving' : ''}>
            {isSaving && (
                <div className="savingOverlay">
                    <div className="savingContent">
                        <div className="bigSpinner"></div>
                        <span>Applying your theme...</span>
                    </div>
                </div>
            )}
            <div className="customColorWrap">
                <div className="colorPickerSection">
                    <strong className="title">Custom Color Picker</strong>
                    <span className="description">Choose any color for your theme</span>
                    <div className="colorInputWrap">
                        <input
                            type="color"
                            value={tempCustomColor}
                            onChange={handleCustomColorChange}
                            className="colorPicker"
                        />
                        <span className="colorValue">{tempCustomColor}</span>
                    </div>
                </div>
            </div>
            <div className="themeWrap">
                <div className="theme">
                    <strong className="title">Manage Theme</strong>
                    {/* <div className="dropdownWrap">
            <div
              className="dropdown"
              ref={dropdownIconRef}
              onClick={() => setIsDropdown((prev) => !prev)}
            >
              <span className="heading">Light mode</span>
              <IoMdArrowDropdown />
            </div>
            <DropDown ref={dropdownRef} className={isDropdown ? "active" : ""}>
              <div className="col">
                <Form form={form} className="form">
                  {dropdownValue.map((value, index) => (
                    <Form.Item
                      key={index}
                      name={value.value}
                      label={value.label}
                      type="checkbox"
                      rounded
                      lg
                      $bgWhite
                    >
                      <Field />
                    </Form.Item>
                  ))}
                </Form>
              </div>
            </DropDown>
          </div> */}
                </div>
                <div className="primaryColor">
                    <strong className="title">Primary Color</strong>
                    <SelectorWrapper>
                        {THEME_COLORS.map((color, index) => (
                            <RadioLabel key={index}>
                                <RadioInput
                                    type="radio"
                                    value={color}
                                    checked={tempSelectedColor === color && isPresetColorSelected}
                                    onChange={e => handleCheckboxChange(e, color)}
                                />
                                <ColorButton color={color}>
                                    {tempSelectedColor === color && isPresetColorSelected && <Checkmark src={tick} alt="checked" />}
                                </ColorButton>
                            </RadioLabel>
                        ))}
                    </SelectorWrapper>
                </div>
                {hasUnsavedChanges && (
                    <div className="actionButtons">
                        <Button 
                            onClick={handleSaveChanges} 
                            className="saveBtn"
                            disabled={!canSaveChanges}
                        >
                            {isSaving ? (
                                <div className="loadingContent">
                                    <div className="spinner"></div>
                                    Applying Theme...
                                </div>
                            ) : 'Save Changes'}
                        </Button>
                        <Button 
                            onClick={handleDiscardChanges} 
                            className="discardBtn" 
                            variant="outline"
                            disabled={isSaving}
                        >
                            Discard
                        </Button>
                    </div>
                )}
            </div>
        </CustomizationWrap>
    );
};

export default Customization;
